import React from 'react'

import Text from 'components_new/atoms/Text'

import { Strings, useTranslation } from 'language/translator'
import { Theme } from '@mui/material'

interface WorkingStatusProps {
  reasoningText?: string
  currentTool?: string | null
}

// Present-tense labels shown while a tool is running.
const TOOL_RUNNING_LABEL_KEYS: Record<string, Strings<'chat'>> = {
  get_metric_schema: 'tool_loading_schema',
  get_underlying_data: 'tool_fetching_data',
  aggregate_metric_by_dimension: 'tool_aggregating'
}

// Picks the last non-empty line of the reasoning stream, so the status line
// always shows the most recent thought.
const latestSnippet = (text?: string) => {
  if (!text) return null

  const lines = text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)

  return lines.length > 0 ? lines[lines.length - 1] : null
}

// Single shimmering status line shown next to the avatar while the assistant
// is working: the running tool wins, then the latest reasoning snippet, then
// a generic "analyzing" fallback.
const WorkingStatus = ({ reasoningText, currentTool }: WorkingStatusProps) => {
  const { t } = useTranslation()

  const toolLabelKey = currentTool
    ? TOOL_RUNNING_LABEL_KEYS[currentTool]
    : undefined
  const status = currentTool
    ? toolLabelKey
      ? t('chat', toolLabelKey)
      : currentTool
    : latestSnippet(reasoningText) ?? t('chat', 'analyzing')

  return (
    <Text
      noWrap={true}
      sx={{
        '@keyframes chat-shimmer': {
          from: { backgroundPosition: '200% 0' },
          to: { backgroundPosition: '-200% 0' }
        },
        animation: 'chat-shimmer 2s linear infinite',
        backgroundClip: 'text',
        backgroundImage: (theme: Theme) =>
          `linear-gradient(90deg, ${theme.palette.text.disabled} 40%, ${theme.palette.text.primary} 50%, ${theme.palette.text.disabled} 60%)`,
        backgroundSize: '200% 100%',
        color: 'transparent',
        minWidth: 0,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
          backgroundImage: 'none',
          color: 'text.secondary',
          WebkitTextFillColor: 'unset'
        }
      }}
      variant="body2"
    >
      {status}
    </Text>
  )
}

export default WorkingStatus
